import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';

import { AssessmentState } from './assessment.interfaces';

@Injectable()
export class AssessmentSelectors {
  public assessment$: Observable<AssessmentState>;

  constructor(private store: Store<any>) {
    this.assessment$ = this.store
      .select(state => state.assessment);
  }

  getAssessmentIsLoaded() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.loaded)
      .filter(success => success)
      .distinctUntilChanged());
  }

  isRicettaByIndexLoaded(idx) {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.metadata.ricette[idx] ? state.metadata.ricette[idx].loaded : false)
      .distinctUntilChanged());
  }

  getAssessmentIsMRFirstAvailableSectionLoaded() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.mrCanStart)
      .distinctUntilChanged());
  }

  getFreezeCount() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.freezeCounter)
      .distinctUntilChanged());
  }

  resetCounterTrigger() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.resetCounterTrigger)
      .distinctUntilChanged());
  }

  getAssessmentMetadata() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.metadata)
      .distinctUntilChanged());
  }

  getAssessmentProgress() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.progress)
      .distinctUntilChanged());
  }

  getAssessmentResults() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.results)
      .distinctUntilChanged());
  }

  getAssessmentQuestionsMap() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.questionsMap)
      .distinctUntilChanged());
  }

  getAssessmentQuestionMapById(id) {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.questionsMap[id])
      .distinctUntilChanged());
  }

  getAssessmentStartStatus() {
    return this.getAssessmentProgress()
      .map(progress => progress.started);
  }

  getAssessmentEndStatus() {
    return this.getAssessmentProgress()
      .map(progress => progress.terminated);
  }

  getCurrentSectionId() {
    return this.getAssessmentProgress()
      .map(progress => progress.currentSectionId);
  }

  getCurrentQuestionId() {
    return this.getAssessmentProgress()
      .map(progress => progress.currentQuestionId);
  }

  getAssessmentError() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.error)
      .filter(error => error !== null)
      .distinctUntilChanged());
  }

  getAssessmentQueue() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.queue)
      .distinctUntilChanged());
  }

  getAssessmentRicetteMetadata() {
    return this.getAssessmentMetadata()
      .filter(metadata => metadata && typeof metadata.ricette !== 'undefined')
      .map(metadata => metadata.ricette);
  }

  getAssessmentLinkFineTest() {
    return this.getAssessmentMetadata()
      .map((metadata) => {
        if (typeof metadata.linkFineTest !== 'undefined') {
          return metadata.linkFineTest;
        }
        return null;
      });
  }

  getAssessmentLoading() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.loading)
      .distinctUntilChanged());
  }

  getAssessmentIsLoadingEnded() {
    return this.getAssessmentLoading()
      .filter(loading => !loading);
  }

  getRicetteDetails() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.ricette)
      .distinctUntilChanged());
  }

  getLastSectionTimeUpdateStatus() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.syncSuccess)
      .distinctUntilChanged());
  }

  getLastSetAnswerStatus() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.setAnswerSuccess)
      .distinctUntilChanged());
  }

  getRicettaDetailById(id) {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.ricette[id])
      .filter(ricetta => typeof ricetta !== 'undefined')
      .distinctUntilChanged());
  }

  getQuestionByMapInfo(ricettaId, turno, domanda) {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.ricette[ricettaId])
      .filter(ricetta => ricetta && ricetta.turni)
      .map(ricetta => {
        if (ricetta.turni[turno] &&
          ricetta.turni[turno].domande) {
            if (domanda === 'T' && ricetta.turni[turno].testoRiferimento) {
              return ricetta.turni[turno].testoRiferimento;
            } else if (ricetta.turni[turno].domande[domanda]) {
              return ricetta.turni[turno].domande[domanda];
            }
          }
      })
      .filter(question => typeof question !== 'undefined')
      .distinctUntilChanged());
  }

  getLoadedRicette() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.loadedRicette)
      .distinctUntilChanged());
  }

  getUserRequestedRicette() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.userRequestedRicette)
      .distinctUntilChanged());
  }

  isRicettaUserRequested(id_ricetta) {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.userRequestedRicette[id_ricetta])
      .filter(requested => requested)
      .distinctUntilChanged());
  }

  getNewSectionLoading() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.newSectionLoading)
      .distinctUntilChanged());
  }

  getNewSectionError() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.newSectionError)
      .distinctUntilChanged());
  }

  getSyncStatus() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.syncStatus)
      .distinctUntilChanged());
  }

  getAssessmentLocked() {
    return this.assessment$
      .let((state$: Observable<AssessmentState>) => state$
      .map(state => state.locked)
      .distinctUntilChanged());
  }
}



// WEBPACK FOOTER //
// ./src/app/logics/assessment/assessment.selectors.ts